import Controller, { OnBeforeCancel, OnBeforeSubmit, OnClick, OnMount, OnUnmount } from '@philips-internal/oswald-ui-core/lib/client';
import { ActionAPI, CheckboxAPI, FetchAPI, FormAPI, LocatorFieldAPI, ModalProps, ModalService, ToastService } from '@philips-internal/oswald-ui-components';
import BaseController from '../../../BaseController';
import { FragmentNames, ScorePewsEditAliases } from '../../../../codegen/index';
import moment from 'moment';
import { ISO_FULL_DATE_FORMAT } from '../../../../constants/date-utils';
import { Inject} from '@odinjs/odin';
import InformationBarUtils from '../../../commons/header/information-bar-header-utils';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { Value } from '@philips-internal/oswald-ui-core';
import { LocatorTypes } from '../../../locator/locator-controller';
import { HttpService,  } from '@philips-internal/oswald-ui-core/lib/services';
import { Endpoint } from '../../../../constants/endpoints';
import { Placement, ToastLevel, ToastType } from '@philips-internal/oswald-ui-constants';
import { AbortableCloseEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import {DomainType, PersonType, ResponseCreateScorePEWS, ScorePEWSResponse} from '../../score-types';
import { ApplicationRoute } from '../../../../constants/routes';
import { BooleanUtils } from '../../../../utils/boolean-utils';
import { generateUUID } from '@philips-internal/oswald-ui-utils';
import DialogService from '../../../../services/dialog-service';
import { ScoreType } from '../../scores-references/score-reference-controller';
import { ScoreListContext } from '../../scores-list/scores-list-service';

@Controller({ component: ScorePewsEditAliases.SCORE_PEWS_ADD_LAYOUT, fragment: FragmentNames.SCORE_PEWS_EDIT })
export default class ScorePewsEditController extends BaseController {

  @Inject(ScorePewsEditAliases.FORM_SCORE_PEWS_ADD)
  private formApi!: FormAPI;

  @Inject(ScorePewsEditAliases.FETCH_PROFESSIONAL)
  private fetchProfessionalApi!: FetchAPI;

  @Inject(ScorePewsEditAliases.LOCATOR_FIELD_PROFESSIONAL)
  private locatorFieldApi!: LocatorFieldAPI;

  @Inject(ScorePewsEditAliases.ACTION_DELETE_SCORE_PEWS)
  private actionDeleteAPI!: ActionAPI;

  @Inject(ScorePewsEditAliases.ACTION_NEW_SCORE_PEWS)
  private actionNewAPI!: ActionAPI;

  @Inject(ScorePewsEditAliases.ACTION_REFERENCE_SCORE_PEWS)
  private actionReferenceAPI!: ActionAPI;

  @Inject(ScorePewsEditAliases.ACTION_RELEASE_SCORE_PEWS)
  private actionReleaseAPI!: ActionAPI;

  @Inject(ScorePewsEditAliases.CHECKBOX_NEBULIZATION)
  private checkboxNebulizationAPI!: CheckboxAPI;

  @Inject(ScorePewsEditAliases.CHECKBOX_VOMIT)
  private checkboxVomitAPI!: CheckboxAPI;

  @OnMount({ target: ScorePewsEditAliases.SCORE_PEWS_ADD_LAYOUT })
  handleFormMount(): void {
    ScoreListContext.currentTab = ScoreType.SCORE_PEWS
    const { encounterId, idScoreIndexes, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.popNavigationItem();
    this.formApi.setLoading(true);
    this.loadEditionData(idScoreIndexes as number)
    this.actionDeleteAPI.setEnabled(false);
    this.actionNewAPI.setEnabled(false);
    this.actionReferenceAPI.setEnabled(false);
    this.actionReleaseAPI.setEnabled(false);

    InformationBarUtils.collapsedBar = true;
    this.context.set({ encounterId, locator: LocatorTypes.PROFESSIONAL_PERSON, patientId });
  }

  @OnUnmount({ target: ScorePewsEditAliases.SCORE_PEWS_ADD_LAYOUT })
  handleUnMountForm(): void {
    ScoreListContext.clean();
    ScoreListContext.currentTab = ScoreType.SCORE_PEWS;
    this.clearAbortController();
  }

  private loadEditionData(sequenceNumber: number): void {
    const abortController = this.setAbortController();
    HttpService.get(Endpoint.SCORE_PEWS, { sequenceNumber }, { abortController })
      .then(response => {
        const record = response.body as ScorePEWSResponse;
        const dateEvaluation = String(record.dateEvaluation);
        this.formApi.setEditing(true);
        const nebulization = BooleanUtils.isTrueValue(record.nebulization);
        const vomit = BooleanUtils.isTrueValue(record.vomit);
        this.checkboxNebulizationAPI.setValue(nebulization);
        this.checkboxVomitAPI.setValue(vomit);
        const person = {
          personCode: record.professionalCode,
          personName: record.professionalName,
        }
        this.fetchProfessionalApi.setEndpoint(Endpoint.GET_PROFESSIONAL_PERSON);
        this.locatorFieldApi.setValue(person.personCode);
        this.formApi.setData({...record, dateEvaluation, nebulization, vomit  });
        this.context.set({nebulization: record.nebulization, vomit: record.vomit});
      }).catch((err) => this.showToast(err?.body.errorMessage, ToastLevel.ERROR))
      .finally(() => {
        this.formApi.setLoading(false)
        this.actionDeleteAPI.setEnabled(true);
        this.actionNewAPI.setEnabled(true);
        this.actionReferenceAPI.setEnabled(true);
        this.actionReleaseAPI.setEnabled(true);
      });
  }

  private releasePEWScore(sequenceNumber: number): void {
    this.formApi.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.put(Endpoint.RELEASE_SCORE_PEWS, { sequenceNumber }, { abortController })
      .then(() => {
        applicationContext.popNavigationItem();
      }).catch((err) => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => {
        this.formApi.setLoading(false);
      });
  }

  private showModalSubmit(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.release-title-evolution',
      onClickCancel: () => { this.goBack() },
      onClickOk: () => { this.releasePEWScore(idScore) },
      titleDialog: 'patient-evolution.release-evolution',
      titleOnCancel: 'patient-evolution.release-cancel-evolution',
      titleOnOk: 'patient-evolution.release-submit-evolution',
    });
  }

  private createNewPEWSScore(body: Record<string,Value>): void {
    this.formApi.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.post(Endpoint.POST_CREATE_SCORE_PEWS, undefined, { abortController, body })
      .then(response => {
        const { idScoreIndexes } = response.body as ResponseCreateScorePEWS;
        this.showModalSubmit(idScoreIndexes);
        this.context.set({ idScoreIndexes, isCreated: true });
      })
      .catch(err => {
        this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
      }).finally(() => this.formApi.setLoading(false));
  }

  private showToast(message: string, level: ToastLevel): void {
    ToastService.open({
      dismissible: true,
      highlight: false,
      id: 'score-pews-create',
      level,
      message,
      placement: Placement.BOTTOM,
      showIcon: true,
      timeout: 5000,
      type: ToastType.BANNER,
    })
  }

  private goBack(): void {
    applicationContext.popNavigationItem();
    applicationContext.popNavigationItem();
  }

  private delete(sequenceNumber: number): void {
    this.formApi.setLoading(true);
    const abortController = this.setAbortController();
    HttpService.delete(Endpoint.SCORE_PEWS, {
      sequenceNumber
    }, { abortController }).then(() => {
      applicationContext.popNavigationItem();
    }).catch((err) => {
      this.showToast(err?.body.errorMessage, ToastLevel.ERROR);
    }).finally(() => {
      this.formApi.setLoading(false);
    });
  }

  private confirmDelete(idScore: number): void {
    DialogService.open({
      descriptionDialog: 'patient-evolution.delete-question-evolution',
      onClickCancel: () => { applicationContext.popNavigationItem() },
      onClickOk: () => {this.delete(idScore);},
      titleDialog: 'patient-evolution.delete-question-evolution',
      titleOnCancel: 'tws.form.cancel',
      titleOnOk: 'patient-evolution.delete-evolution',
    });
  }

  @OnBeforeCancel({ target: ScorePewsEditAliases.FORM_SCORE_PEWS_ADD })
  onCancelForm(event: AbortableCloseEvent): void {
    event.abort();
    this.discardChanges();
  }

  @OnBeforeSubmit({ target: ScorePewsEditAliases.FORM_SCORE_PEWS_ADD })
  handleBeforeSubmit(): void {
    const isCreated = this.context.get('isCreated');
    const {encounterId, idScoreIndexes} = this.historyService.getCurrentRouteParams();
    if (isCreated) {
      this.showModalSubmit(idScoreIndexes as number);
      return;
    }
    const formData = this.formApi.getRecord();
    const nebulization = BooleanUtils.isPositiveValue(formData.nebulization as boolean);
    const vomit = BooleanUtils.isPositiveValue(formData.vomit as boolean);

    const evaluationDate = moment(formData.dateEvaluation as string).utc().format(ISO_FULL_DATE_FORMAT);
    const cardiovascular = formData.cardiovascular as DomainType;
    const neurologicalStatus = formData.neurologicalStatus as DomainType;
    const respiratory = formData.respiratory as DomainType;
    const person = formData.person as PersonType;
    const body = {
      attendance: encounterId as number,
      cardiovascular: cardiovascular ? cardiovascular.valueDomain : '',
      evaluationDate,
      idScoreIndexes: idScoreIndexes as number,
      nebulization,
      neurologicalStatus: neurologicalStatus ? neurologicalStatus.valueDomain : '',
      professionalCode: person.personCode,
      respiratory: respiratory ? respiratory.valueDomain : '',
      vomit,
    }
    this.createNewPEWSScore(body);
  }

  @OnClick({ target: ScorePewsEditAliases.ACTION_NEW_SCORE_PEWS })
  handleClickNew(): void {
    const { encounterId, patientId } = this.historyService.getCurrentRouteParams();
    applicationContext.redirect(ApplicationRoute.SCORE_PEWS_CREATE, {
      encounterId,
      patientId,
    })
  }

  private getReference(): void {
    const scoreType = 'pews';
    const modalProperties: ModalProps = {
      closeable: true,
      closeByDocument: false,
      closeByEscape: false,
      focusOnOpen: false,
      fragment: 'score-reference',
      id: 'score-reference',
      onCancel: () => { },
      onSubmit: () => { },
      title: 'tws.reference-pain-scale-value',
      uuid: generateUUID(),
    };

    modalProperties.contextData = {
      scoreType,
    }

    ModalService.openFullscreen(modalProperties);
  }

  private discardChanges(): void {
    DialogService.open({
      descriptionDialog: 'clinical-notes.discard-changes',
      onClickCancel: () => {},
      onClickOk: () => {
        applicationContext.popNavigationItem();
        applicationContext.popNavigationItem();
      },
      titleDialog: 'clinical-notes.discard-changes',
      titleOnCancel: 'patient-evolution.cancel',
      titleOnOk: 'clinical-notes.discard-submit'
    });
  }

  @OnClick({ target: ScorePewsEditAliases.ACTION_RELEASE_SCORE_PEWS })
  handleClickRelease(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.showModalSubmit(idScoreIndexes as number);
  }

  @OnClick({ target: ScorePewsEditAliases.ACTION_DELETE_SCORE_PEWS })
  handleClickDelete(): void {
    const { idScoreIndexes } = this.historyService.getCurrentRouteParams();
    this.confirmDelete(idScoreIndexes as number);
  }

  @OnClick({ target: ScorePewsEditAliases.ACTION_REFERENCE_SCORE_PEWS })
  handleClickReference(): void {
    this.getReference();
  }

}
